// ─────────────────────────────────────────────────────────────────────────────────────────────
// pvsim.js — Motor de SIMULACIÓN HORARIA (8760 h), núcleo de diseño de grado ingeniería.
// Funciones PURAS (sin red/React, sin imports relativos) → viven en `src/motores/` para que el
// backend propio las IMPORTE en vez de llevar una copia (la clase de bug que costó tres incidentes).
//
// Dos motores, un mismo modelo físico:
//   · `simulateYear`      — sintético: reparte la irradiancia MENSUAL (PVGIS/NASA) sobre el día
//                           representativo con descomposición directa/difusa (Erbs). No necesita
//                           TMY, así que es el que sirve `pv-estimate` (contrato de la suite).
//   · `simulateFromHourly`— con la serie TMY HORARIA REAL del sitio y la cadena de pérdidas
//                           FÍSICA Y GRANULAR: horizonte → filas/GCR → IAM → soiling → conversión
//                           STC → temperatura de celda (NOCT) → LID → calidad → mismatch →
//                           óhmicas DC → inversor + clipping → óhmicas AC → indisponibilidad.
//                           Cada etapa se ACUMULA en kWh, así que el diagrama de pérdidas sale
//                           CALCULADO (no repartido con pesos típicos) y el PR es un RESULTADO.
//                           Devuelve además balances mensuales, serie diaria e histograma de potencia.
//
// Es el motor que ensambla el ESTUDIO en Solar Hub (no embebido): la ingeniería vive acá y el dato
// medido (telemetría AMI, pliego) se CONSUME de FluxAI por el espejo `/hub-api/flux`.

const RAD = Math.PI / 180;
const G_SC = 1367; // constante solar (W/m²)
// Irradiancia mensual por defecto (Bogotá-ish) si no se provee — kWh/m²/día por mes.
const GHI_DEFECTO = [4.6, 4.7, 4.6, 4.3, 4.2, 4.1, 4.3, 4.4, 4.5, 4.3, 4.2, 4.4];
// Día representativo de cada mes (día del año) y nº de días por mes.
const DIA_REP = [17, 47, 75, 105, 135, 162, 198, 228, 258, 288, 318, 344];
const DIAS_MES = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Declinación solar (°) para el día del año n.
function declinacion(n) { return 23.45 * Math.sin(RAD * 360 * (284 + n) / 365); }
// Factor de corrección de distancia Tierra-Sol.
function e0(n) { return 1 + 0.033 * Math.cos(RAD * 360 * n / 365); }

/**
 * Posición solar a una hora solar (lat en °, decl en °, hora 0..24).
 * Devuelve cos(zenit), elevación (°) y azimut solar (° desde el Sur, + Oeste).
 */
export function posicionSolar(latDeg, declDeg, hora) {
  const lat = latDeg * RAD, decl = declDeg * RAD;
  const omega = 15 * (hora - 12) * RAD; // ángulo horario
  const cosZ = Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(omega);
  const cosZc = Math.max(-1, Math.min(1, cosZ));
  const zen = Math.acos(cosZc);
  const elev = 90 - zen / RAD;
  // Azimut desde el Sur, positivo hacia el Oeste.
  const az = Math.atan2(Math.sin(omega), Math.cos(omega) * Math.sin(lat) - Math.tan(decl) * Math.cos(lat)) / RAD;
  return { cosZ: Math.max(0, cosZ), elev, az };
}

// Fracción difusa de Erbs a partir del índice de claridad kt.
function fraccionDifusa(kt) {
  if (kt <= 0.22) return 1 - 0.09 * kt;
  if (kt <= 0.80) return 0.9511 - 0.1604 * kt + 4.388 * kt ** 2 - 16.638 * kt ** 3 + 12.336 * kt ** 4;
  return 0.165;
}

/**
 * Simula un AÑO horario (8760 h) y devuelve energía AC + métricas.
 * opts:
 *   lat (°), kwp, tilt (°), azimuth (° desde el Sur, + Oeste; 0 = Sur),
 *   dcac (ratio DC/AC del inversor), monthlyGHI ([12] kWh/m²/día), monthlyTamb ([12] °C, opc),
 *   albedo (0.2), gammaPmax (-0.0035/°C), noct (45 °C), etaInv (0.97), perdidas (0.14 = soiling+
 *   cableado+mismatch...), incluirHoras (bool → devuelve el arreglo 8760).
 */
export function simulateYear(opts = {}) {
  const lat = Number(opts.lat ?? 4.6);
  const kwp = Number(opts.kwp ?? 1);
  const tilt = Number(opts.tilt ?? Math.max(5, Math.abs(lat))) * RAD;
  const azArr = Number(opts.azimuth ?? 0) * RAD;
  const dcac = Number(opts.dcac ?? 1.2);
  const ghiMes = Array.isArray(opts.monthlyGHI) && opts.monthlyGHI.length === 12 ? opts.monthlyGHI : GHI_DEFECTO;
  const tambMes = Array.isArray(opts.monthlyTamb) && opts.monthlyTamb.length === 12 ? opts.monthlyTamb : null;
  const albedo = Number(opts.albedo ?? 0.2);
  const gamma = Number(opts.gammaPmax ?? -0.0035);
  const noct = Number(opts.noct ?? 45);
  const etaInv = Number(opts.etaInv ?? 0.97);
  const perdidas = Number(opts.perdidas ?? 0.14); // pérdidas DC fijas (soiling, cableado, mismatch, LID)
  const pacMax = kwp / dcac; // potencia AC máxima (kW) → recorte

  const cosB = Math.cos(tilt), sinB = Math.sin(tilt);
  const monthlyKwh = new Array(12).fill(0);
  const horas = opts.incluirHoras ? [] : null;
  let poaAnual = 0, clipKwh = 0, dcKwh = 0;

  for (let m = 0; m < 12; m++) {
    const n = DIA_REP[m];
    const decl = declinacion(n);
    const Gon = G_SC * e0(n);
    // 1) Pesos horarios (cos zenit) del día representativo → reparte el GHI diario del mes.
    const pos = [], pesos = [];
    let sumPeso = 0;
    for (let h = 0; h < 24; h++) {
      const p = posicionSolar(lat, decl, h + 0.5);
      pos.push(p);
      const w = p.cosZ; pesos.push(w); sumPeso += w;
    }
    const ghiDiaWh = ghiMes[m] * 1000; // Wh/m²/día
    const tamb = tambMes ? tambMes[m] : 25;
    let poaDiaWh = 0, acDiaWh = 0, dcDiaWh = 0, clipDiaWh = 0;
    for (let h = 0; h < 24; h++) {
      if (sumPeso <= 0 || pesos[h] <= 0) continue;
      const p = pos[h];
      const ghi = ghiDiaWh * pesos[h] / sumPeso;       // W·h en esa hora ≈ W medios (Δt=1h)
      // 2) Descomposición directa/difusa (Erbs) con kt horario.
      const g0 = Gon * p.cosZ;                          // extraterrestre horizontal
      const kt = g0 > 0 ? Math.max(0, Math.min(1, ghi / g0)) : 0;
      const kd = fraccionDifusa(kt);
      const dif = ghi * kd, dir = ghi - dif;            // difusa / directa horizontal
      // 3) Transposición al plano (isotrópico de Liu-Jordan).
      const cosAOI = Math.max(0, p.cosZ * cosB + Math.sqrt(Math.max(0, 1 - p.cosZ * p.cosZ)) * sinB * Math.cos((p.az * RAD) - azArr));
      const Rb = p.cosZ > 0.01 ? cosAOI / p.cosZ : 0;
      const poa = dir * Rb + dif * (1 + cosB) / 2 + ghi * albedo * (1 - cosB) / 2;
      if (poa <= 0) continue;
      poaDiaWh += poa;
      // 4) Temperatura de celda (NOCT) y potencia DC.
      const tc = tamb + (noct - 20) / 800 * poa;
      const pdc = kwp * (poa / 1000) * (1 + gamma * (tc - 25)) * (1 - perdidas); // kW
      const pdcPos = Math.max(0, pdc);
      dcDiaWh += pdcPos * 1000;
      // 5) Inversor con recorte (clipping).
      const pac = Math.min(pdcPos * etaInv, pacMax);
      acDiaWh += pac * 1000;
      const clip = Math.max(0, pdcPos * etaInv - pacMax);
      clipDiaWh += clip * 1000;
      if (horas) horas.push({ m, h, poa: +poa.toFixed(1), ac: +pac.toFixed(4) });
    }
    monthlyKwh[m] = acDiaWh * DIAS_MES[m] / 1000;       // kWh del mes
    poaAnual += poaDiaWh * DIAS_MES[m] / 1000;          // kWh/m²
    dcKwh += dcDiaWh * DIAS_MES[m] / 1000;
    clipKwh += clipDiaWh * DIAS_MES[m] / 1000;
  }

  const annualKwh = monthlyKwh.reduce((a, b) => a + b, 0);
  const specificYield = kwp > 0 ? annualKwh / kwp : 0;             // kWh/kWp/año
  // PR = rendimiento (kWh/kWp) ÷ rendimiento de referencia (POA en kWh/m² ÷ 1 kW/m² STC).
  const pr = poaAnual > 0 && kwp > 0 ? (annualKwh / kwp) / poaAnual : 0;
  const capacityFactor = kwp > 0 ? annualKwh / (kwp * 8760) : 0;
  return {
    annualKwh: Math.round(annualKwh),
    monthlyKwh: monthlyKwh.map((v) => Math.round(v)),
    specificYield: Math.round(specificYield),
    pr: +pr.toFixed(3),
    capacityFactor: +capacityFactor.toFixed(3),
    poaAnnual: Math.round(poaAnual),                  // kWh/m²/año en el plano
    clippingKwh: Math.round(clipKwh),
    clippingPct: dcKwh > 0 ? +((clipKwh / (dcKwh * etaInv)) * 100).toFixed(2) : 0,
    ...(horas ? { horas } : {}),
  };
}

// ── SOMBRAS (shading) ────────────────────────────────────────────────────────────────────────
// Far-shading (horizonte) + near-shading (entre filas por GCR). Aproximación pragmática,
// sin geometría 3D: el 3D por obstáculo se hará con un editor de escena aparte.

/**
 * Fracción del haz directo perdida por sombra ENTRE FILAS (near-shading) para una posición solar.
 * tiltDeg: inclinación; gcr (0..1): ground coverage ratio = largo módulo / paso entre filas;
 * sunElevDeg: elevación solar; sunAzRelDeg: azimut solar relativo al arreglo (°, 0 = de frente).
 * Modelo de ángulo de perfil (clásico de filas): s/L = 1 − (1/gcr)·sin ψ / sin(β+ψ).
 */
export function interRowShadedFraction(tiltDeg, gcr, sunElevDeg, sunAzRelDeg) {
  if (!(gcr > 0 && gcr < 1)) return 0;       // sin info de GCR → sin sombra entre filas
  if (sunElevDeg <= 0) return 1;             // sol bajo el horizonte
  const cosRel = Math.cos(sunAzRelDeg * RAD);
  if (cosRel <= 0) return 0;                 // sol por detrás del plano (lo cubre el AOI)
  const psi = Math.atan(Math.tan(sunElevDeg * RAD) / cosRel); // ángulo de perfil
  const beta = tiltDeg * RAD;
  const s = 1 - (1 / gcr) * Math.sin(psi) / Math.sin(beta + psi);
  return Math.max(0, Math.min(1, s));
}

/**
 * Convierte OBSTÁCULOS cercanos (cajas simples) + un horizonte manual en un perfil de horizonte
 * de 72 sectores (cada 5°) que el motor consume vía `horizonElevacion`. Cada obstáculo eleva el
 * horizonte a atan(alto/distancia) en el rango de azimut que subtiende (± semiángulo por ancho).
 * obstaculo: { azimuth(° desde Sur,+O), distancia(m), alto(m), ancho(m) }.
 */
export function obstaclesToHorizon(obstaculos = [], manual = []) {
  const STEP = 5, N = 72;
  const elevAt = new Array(N).fill(0);
  const idx = (az) => { const a = ((az + 180) % 360 + 360) % 360; return Math.floor(a / STEP) % N; };
  const raise = (az, el) => { const i = idx(az); if (el > elevAt[i]) elevAt[i] = el; };
  for (const m of manual || []) raise(Number(m.az) || 0, Number(m.elev) || 0);
  for (const o of obstaculos || []) {
    const dist = Math.max(0.1, Number(o.distancia) || 0);
    const alto = Math.max(0, Number(o.alto) || 0);
    const ancho = Math.max(0, Number(o.ancho) || 0);
    if (alto <= 0) continue;
    const el = Math.atan2(alto, dist) * 180 / Math.PI;
    const half = Math.atan2(ancho / 2, dist) * 180 / Math.PI;
    const oAz = Number(o.azimuth) || 0;
    raise(oAz, el);
    for (let a = oAz - half; a <= oAz + half; a += STEP) raise(a, el);
  }
  const out = [];
  for (let i = 0; i < N; i++) out.push({ az: -180 + i * STEP, elev: +elevAt[i].toFixed(1) });
  return out;
}

/** Elevación del horizonte (°) en un azimut dado, interpolando el perfil [{az,elev}] (az desde Sur,+O). */
export function horizonElevacion(horizon, azDeg) {
  if (!Array.isArray(horizon) || !horizon.length) return 0;
  let best = horizon[0], bestD = Infinity;
  for (const p of horizon) {
    const d = Math.abs(((p.az - azDeg + 540) % 360) - 180); // distancia angular
    if (d < bestD) { bestD = d; best = p; }
  }
  return Number(best.elev) || 0;
}

// ── PÉRDIDAS GRANULARES ──────────────────────────────────────────────────────────────────────
// El motor acepta `perdidas` de DOS formas y esa dualidad es deliberada:
//   · NÚMERO (lo que manda el cotizador hoy: 0.14) = un solo bulto de pérdidas DC. Se normaliza a
//     `{ ohmicaDc: n, resto: 0 }`, con lo cual la cadena granular se REDUCE exactamente a la
//     matemática vieja → la energía de una cotización ya emitida NO cambia. Esto no es cortesía:
//     un motor que altera en silencio los kWh de un estudio entregado rompe la propuesta comercial.
//   · OBJETO = las 8 pérdidas por separado, cada una con su etapa en el diagrama de pérdidas.
// El default sigue siendo 0.14 (numérico) a propósito: los defaults granulares suman ~12-13% y
// cambiarlo movería el número de todos los llamadores que no pasan nada.
export const PERDIDAS_DEFECTO = {
  soiling: 0.03,          // suciedad sobre el vidrio
  iamB0: 0.05,            // coef. ASHRAE del modificador por ángulo de incidencia
  lid: 0.02,              // degradación inducida por luz (1er año)
  calidadModulo: 0,       // desviación vs placa
  mismatch: 0.02,         // dispersión entre módulos y strings
  ohmicaDc: 0.015,        // caída óhmica DC a STC
  ohmicaAc: 0.01,         // caída óhmica AC inversor → frontera
  indisponibilidad: 0.01, // fracción de tiempo fuera de servicio
};

const CERO_PERDIDAS = {
  soiling: 0, iamB0: 0, lid: 0, calidadModulo: 0,
  mismatch: 0, ohmicaDc: 0, ohmicaAc: 0, indisponibilidad: 0,
};

/**
 * Normaliza `perdidas` a la forma granular. Un número entra COMPLETO como óhmica DC (única etapa
 * que multiplica la potencia DC ya convertida, igual que el `(1 - perdidas)` del modelo viejo) y
 * las demás quedan en 0 → misma energía, con la etapa rotulada honestamente como "pérdidas DC".
 */
export function normalizarPerdidas(p) {
  if (p == null) return { ...CERO_PERDIDAS, ohmicaDc: 0.14, agregada: true };
  if (typeof p === 'number') return { ...CERO_PERDIDAS, ohmicaDc: Number(p) || 0, agregada: true };
  return { ...PERDIDAS_DEFECTO, ...p, agregada: false };
}

/**
 * Simula a partir de una SERIE HORARIA REAL (TMY de PVGIS) con la cadena de pérdidas FÍSICA y
 * GRANULAR: horizonte (far-shading) → filas/GCR (near) → IAM → soiling → conversión STC →
 * temperatura de celda (NOCT) → LID → calidad → mismatch → óhmicas DC → inversor + clipping →
 * óhmicas AC → indisponibilidad. Cada etapa se ACUMULA en kWh, así que el diagrama de pérdidas
 * sale CALCULADO (no repartido con pesos típicos) y el PR es un RESULTADO, no un input.
 *
 * hourly: [{ doy(1..365), hour(0..23), month(0..11), ghi, dni, dhi, tamb }]
 *
 * ⚠️ Un solo número cambia respecto al modelo anterior, y SOLO si hay sombras declaradas
 * (`horizon` u obstáculos, o `gcr > 0`): `poaAnnual` pasa a ser la irradiación transpuesta ANTES
 * de sombras (convención GlobInc), no después. Consecuencia: el `pr` de un sitio sombreado BAJA,
 * porque la sombra deja de esconderse en el denominador y pasa a verse como lo que es — una
 * pérdida de rendimiento, con su propia línea en la cascada. Sin sombras, ambos son idénticos.
 * La energía (`annualKwh`, `monthlyKwh`, `specificYield`) NO cambia en ningún caso.
 */
export function simulateFromHourly(opts = {}) {
  const lat = Number(opts.lat ?? 4.6), kwp = Number(opts.kwp ?? 1);
  const tiltDeg = Number(opts.tilt ?? Math.max(5, Math.abs(lat)));
  const azDeg = Number(opts.azimuth ?? 0);
  const tilt = tiltDeg * RAD, azArr = azDeg * RAD;
  const dcac = Number(opts.dcac ?? 1.2);
  const albedo = Number(opts.albedo ?? 0.2), gamma = Number(opts.gammaPmax ?? -0.0035);
  const noct = Number(opts.noct ?? 45), etaInv = Number(opts.etaInv ?? 0.97);
  const P = normalizarPerdidas(opts.perdidas);
  const gcr = Number(opts.gcr) || 0;              // near-shading entre filas (0 = sin sombra)
  const horizon = Array.isArray(opts.horizon) ? opts.horizon : null;  // far-shading (perfil de horizonte)
  const hourly = Array.isArray(opts.hourly) ? opts.hourly : [];
  if (!hourly.length) throw new Error('simulateFromHourly: serie horaria vacía');
  // 🔴 GUARD DE SERIE. Sin él, una fila sin `doy` da `declinacion(undefined)` = NaN, y como
  // `NaN <= 0` es FALSO ninguno de los `continue` de abajo la descarta: el NaN se propaga hasta
  // `annualKwh` y el llamador pinta un resultado con autoridad de modelo horario. Pasó de verdad
  // (el proxy `pvgis-tmy` entregaba `{time, t2m}` en vez de `{doy, hour, tamb}`). Un error mudo es
  // peor que un rechazo: acá se rechaza, con el nombre del campo que falta.
  const m0 = hourly[0] || {};
  const faltan = ['doy', 'hour'].filter((k) => !Number.isFinite(Number(m0[k])));
  if (faltan.length) {
    throw new Error(
      `simulateFromHourly: la serie horaria no trae ${faltan.join(' ni ')} (fila 0). ` +
      'Se espera { doy, hour, month, ghi, dni, dhi, tamb }.'
    );
  }
  const cosB = Math.cos(tilt), sinB = Math.sin(tilt), pacMax = kwp / dcac;

  // Factores estáticos DC — se aplican por hora ANTES del inversor (el clipping depende de ellos).
  const fLid = 1 - P.lid, fCal = 1 - P.calidadModulo, fMis = 1 - P.mismatch, fOhmDc = 1 - P.ohmicaDc;

  // Acumuladores de la CASCADA: irradiación en kWh/m², energías en kWh.
  let ghiSum = 0, poaBruta = 0, sombraIrr = 0, iamIrr = 0, soilIrr = 0;
  let eStc = 0, eTemp = 0, eArray = 0, eInvEf = 0, eClip = 0;
  const monthlyKwh = new Array(12).fill(0);
  const bal = Array.from({ length: 12 }, () => ({ ghi: 0, poa: 0, tambSum: 0, n: 0, eArr: 0, eAc: 0 }));
  const diarioPoa = new Array(366).fill(0), diarioAc = new Array(366).fill(0);
  const NBINS = 20, hist = new Array(NBINS).fill(0);

  // Serie AC hora a hora — OPT-IN (`devolverHoraria`). No se devuelve siempre a propósito: son
  // 8760 números que, si se guardan con la cotización, inflan cada fila de la base sin que nadie
  // los consulte. El llamador la pide, calcula el balance con la carga y se queda con el RESUMEN.
  const serieAc = opts.devolverHoraria ? new Array(hourly.length).fill(0) : null;

  for (let i = 0; i < hourly.length; i++) {
    const r = hourly[i];
    const p = posicionSolar(lat, declinacion(r.doy), r.hour + 0.5);
    const dniMeteo = Math.max(0, Number(r.dni) || 0);
    const dhi = r.dhi != null ? Math.max(0, Number(r.dhi)) : Math.max(0, (Number(r.ghi) || 0) - dniMeteo * p.cosZ);
    const ghi = r.ghi != null ? Math.max(0, Number(r.ghi)) : dniMeteo * p.cosZ + dhi;
    const m = r.month != null && r.month >= 0 && r.month < 12 ? r.month : 0;
    bal[m].tambSum += r.tamb != null ? Number(r.tamb) : 25; bal[m].n++;
    if (ghi > 0) { ghiSum += ghi / 1000; bal[m].ghi += ghi / 1000; }
    if (p.cosZ <= 0) continue;

    const cosAOI = Math.max(0, p.cosZ * cosB + Math.sqrt(Math.max(0, 1 - p.cosZ * p.cosZ)) * sinB * Math.cos((p.az * RAD) - azArr));

    // 1) TRANSPOSICIÓN (POA bruta, sin sombras ni óptica) — es la GlobInc del balance.
    const poa0 = dniMeteo * cosAOI + dhi * (1 + cosB) / 2 + ghi * albedo * (1 - cosB) / 2;
    if (poa0 <= 0) continue;
    poaBruta += poa0 / 1000; bal[m].poa += poa0 / 1000;
    if (r.doy >= 1 && r.doy <= 366) diarioPoa[r.doy - 1] += poa0 / 1000;

    // 2) SOMBRAS sobre el haz directo: horizonte (lo bloquea entero) + entre filas (fracción).
    let dni = dniMeteo;
    if (horizon && horizonElevacion(horizon, p.az) > p.elev) dni = 0;
    if (gcr > 0 && dni > 0) dni *= (1 - interRowShadedFraction(tiltDeg, gcr, p.elev, p.az - azDeg));
    sombraIrr += (dniMeteo - dni) * cosAOI / 1000;

    // 3) IAM (ASHRAE) sobre el haz directo que sobrevive: 1 − b0·(1/cosAOI − 1), acotado a [0,1].
    //    Sin coeficiente (b0 = 0) NO hay modelo de IAM → iam = 1; el guard de rasante solo aplica
    //    cuando el modelo está activo, o un b0 nulo alteraría la energía en la banda rasante.
    const iam = P.iamB0 > 0
      ? (cosAOI > 0.01 ? Math.max(0, Math.min(1, 1 - P.iamB0 * (1 / cosAOI - 1))) : 0)
      : 1;
    iamIrr += dni * cosAOI * (1 - iam) / 1000;

    // 4) SOILING sobre toda la irradiancia que llega al vidrio.
    const poaOptica = dni * cosAOI * iam + dhi * (1 + cosB) / 2 + ghi * albedo * (1 - cosB) / 2;
    soilIrr += poaOptica * P.soiling / 1000;
    const poaEf = poaOptica * (1 - P.soiling);          // GlobEff (W/m²)
    if (poaEf <= 0) continue;

    // 5) CONVERSIÓN a energía DC nominal (eficiencia STC implícita en el kWp).
    const pStc = kwp * poaEf / 1000;
    eStc += pStc;

    // 6) TEMPERATURA de celda (NOCT) → efecto térmico (pérdida, o ganancia si Tc < 25 °C).
    const tc = (r.tamb != null ? Number(r.tamb) : 25) + (noct - 20) / 800 * poaEf;
    const pTemp = Math.max(0, pStc * (1 + gamma * (tc - 25)));
    eTemp += pTemp;

    // 7) LID + calidad + mismatch + óhmicas DC → energía virtual del arreglo (EArray).
    const pArr = pTemp * fLid * fCal * fMis * fOhmDc;
    eArray += pArr; bal[m].eArr += pArr;

    // 8) INVERSOR: eficiencia + recorte (clipping) a la potencia AC nominal.
    const pacSin = pArr * etaInv;
    eInvEf += pArr - pacSin;
    const pac = Math.min(pacSin, pacMax);
    eClip += Math.max(0, pacSin - pacMax);
    monthlyKwh[m] += pac; bal[m].eAc += pac;
    if (r.doy >= 1 && r.doy <= 366) diarioAc[r.doy - 1] += pac;
    if (serieAc) serieAc[i] = pac;
    if (pac > 0 && pacMax > 0) hist[Math.min(NBINS - 1, Math.floor((pac / pacMax) * NBINS))] += pac;
  }

  // 9) Óhmicas AC + indisponibilidad — factores anuales sobre la salida del inversor.
  const fAc = (1 - P.ohmicaAc) * (1 - P.indisponibilidad);
  const eInvOut = monthlyKwh.reduce((a, b) => a + b, 0);

  // Normaliza si la serie no cubre exactamente 1 año (TMY = 8760 h; escala si difiere).
  const factor = 8760 / hourly.length;
  const annualKwh = eInvOut * fAc * factor;
  const poaAnual = poaBruta * factor;
  const globHor = ghiSum * factor;
  const specificYield = kwp > 0 ? annualKwh / kwp : 0;
  const pr = poaAnual > 0 && kwp > 0 ? specificYield / poaAnual : 0;   // PR CALCULADO

  // ── CASCADA: diagrama de pérdidas CALCULADO, etapa por etapa ────────────────────────────────
  const pct = (perdida, base) => (base > 0 ? +(-(perdida / base) * 100).toFixed(2) : 0);
  const irrPostSombra = poaBruta - sombraIrr, irrPostIam = irrPostSombra - iamIrr, globEff = irrPostIam - soilIrr;
  const cascada = [
    { etiqueta: 'Irradiación global horizontal', kwh: Math.round(globHor), perdidaPct: 0, unidad: 'kWh/m²' },
    { etiqueta: `Transposición al plano (${Math.round(tiltDeg)}°/${Math.round(azDeg)}°)`, kwh: Math.round(poaAnual), perdidaPct: ghiSum > 0 ? +(((poaBruta - ghiSum) / ghiSum) * 100).toFixed(2) : 0, unidad: 'kWh/m²' },
    { etiqueta: 'Sombras (horizonte + filas)', kwh: Math.round(irrPostSombra * factor), perdidaPct: pct(sombraIrr, poaBruta), unidad: 'kWh/m²' },
    { etiqueta: 'IAM (ángulo de incidencia)', kwh: Math.round(irrPostIam * factor), perdidaPct: pct(iamIrr, irrPostSombra), unidad: 'kWh/m²' },
    { etiqueta: 'Soiling (suciedad)', kwh: Math.round(globEff * factor), perdidaPct: pct(soilIrr, irrPostIam), unidad: 'kWh/m²' },
    { etiqueta: 'Energía nominal del arreglo (efic. STC)', kwh: Math.round(eStc * factor), perdidaPct: 0, unidad: 'kWh' },
    { etiqueta: 'Temperatura del módulo', kwh: Math.round(eTemp * factor), perdidaPct: eStc > 0 ? +(((eTemp - eStc) / eStc) * 100).toFixed(2) : 0, unidad: 'kWh' },
    {
      // Con `perdidas` numérica no hay desglose que mostrar: la etiqueta lo DICE en vez de fingir
      // un detalle que el llamador nunca entregó.
      etiqueta: P.agregada ? 'Pérdidas DC del sistema (agregadas)' : 'LID + calidad + mismatch + óhmicas DC',
      kwh: Math.round(eArray * factor), perdidaPct: eTemp > 0 ? +(((eArray - eTemp) / eTemp) * 100).toFixed(2) : 0, unidad: 'kWh',
    },
    { etiqueta: 'Eficiencia del inversor', kwh: Math.round((eArray - eInvEf) * factor), perdidaPct: pct(eInvEf, eArray), unidad: 'kWh' },
    { etiqueta: 'Recorte del inversor (clipping)', kwh: Math.round(eInvOut * factor), perdidaPct: pct(eClip, eArray - eInvEf), unidad: 'kWh' },
    { etiqueta: 'Óhmicas AC + indisponibilidad', kwh: Math.round(annualKwh), perdidaPct: +(-((1 - fAc) * 100)).toFixed(2), unidad: 'kWh' },
  ];

  const balances = bal.map((b, i) => {
    const eAcMes = b.eAc * fAc * factor;
    return {
      mes: i,
      globHorKwhM2: Math.round(b.ghi * factor * 10) / 10,
      globIncKwhM2: Math.round(b.poa * factor * 10) / 10,
      tambC: b.n > 0 ? Math.round((b.tambSum / b.n) * 10) / 10 : 0,
      eArrayKwh: Math.round(b.eArr * factor),
      eGridKwh: Math.round(eAcMes),
      pr: b.poa > 0 && kwp > 0 ? Math.round((eAcMes / kwp / (b.poa * factor)) * 1000) / 1000 : 0,
    };
  });

  // Gráficas predefinidas: serie diaria entrada/salida + distribución de la potencia AC.
  const diario = Array.from({ length: 365 }, (_, i) => ({
    globInc: Math.round(diarioPoa[i] * 100) / 100,
    eGrid: Math.round(diarioAc[i] * fAc * 10) / 10,
  }));
  const histPot = Array.from({ length: NBINS }, (_, i) => ({
    pctPn: Math.round(((i + 0.5) / NBINS) * 100),
    kwh: Math.round(hist[i] * fAc * factor),
  }));

  const sombraPct = poaBruta > 0 ? +((sombraIrr / poaBruta) * 100).toFixed(2) : 0;
  return {
    // ── Contrato histórico (lo que ya consume el cotizador) ──
    annualKwh: Math.round(annualKwh),
    monthlyKwh: monthlyKwh.map((v) => Math.round(v * fAc * factor)),
    specificYield: Math.round(specificYield),
    pr: +pr.toFixed(3),
    capacityFactor: kwp > 0 ? +(annualKwh / (kwp * 8760)).toFixed(3) : 0,
    poaAnnual: Math.round(poaAnual),
    clippingKwh: Math.round(eClip * factor),
    clippingPct: eArray > 0 ? +((eClip / (eArray * etaInv)) * 100).toFixed(2) : 0,
    shadingPct: sombraPct,
    fuente: 'tmy',
    horasSerie: hourly.length,
    // ── Ingeniería de detalle (lo que habilita el estudio de alto nivel) ──
    globHorAnual: Math.round(globHor),
    sombraPct,
    cascada,
    balances,
    diario,
    histPot,
    pacKw: Math.round(pacMax * 10) / 10,
    perdidasAplicadas: P,
    tieneHorizonte: !!(horizon && horizon.length),
    // Serie AC horaria entregada a la red/carga, ya con las pérdidas AC aplicadas. Solo si se pidió.
    horariaKwh: serieAc ? serieAc.map((v) => v * fAc) : null,
  };
}

/**
 * Escenarios de probabilidad de producción (P50 / P75 / P90).
 *
 * Vive ACÁ y no en la pantalla a propósito: el mismo número lo muestran la vista del cotizador y la
 * memoria de cálculo que se le entrega al OR. Dos implementaciones del mismo cálculo terminan
 * divergiendo — es exactamente lo que pasó con el diagrama de pérdidas (tres versiones, la que veía
 * el cliente armada con literales). Una función pura, un solo número.
 *
 * P50 = la producción esperada (la que entrega el motor). P90 = la que se excede con 90% de
 * probabilidad; es la cifra BANCABLE, la que mira un financiador. La incertidumbre combina la
 * variabilidad interanual del RECURSO con la del MODELO, en cuadratura (son independientes):
 * σ = √(σ_recurso² + σ_modelo²).
 *
 * σ_recurso por defecto = 6.5%: si se dispone del histórico interanual real del sitio (PVGIS MRcalc),
 * se pasa el σ MEDIDO y el P90 deja de ser un supuesto.
 */
export function escenariosProbabilidad(annualKwh, opts = {}) {
  const p50 = Math.round(Number(annualKwh) || 0);
  const sRec = Number.isFinite(opts.sigmaRecurso) ? opts.sigmaRecurso : 0.065;
  const sMod = Number.isFinite(opts.sigmaModelo) ? opts.sigmaModelo : 0.05;
  const sigma = Math.sqrt(sRec * sRec + sMod * sMod);
  return {
    p50,
    p75: Math.round(p50 * (1 - 0.674 * sigma)), // z(75%) = 0.674
    p90: Math.round(p50 * (1 - 1.282 * sigma)), // z(90%) = 1.282
    sigma: +sigma.toFixed(4),
    sigmaRecurso: sRec,
    sigmaModelo: sMod,
    // Si el σ del recurso vino MEDIDO del histórico, se declara: cambia cómo se lee el P90.
    recursoMedido: !!opts.recursoMedido,
  };
}

export default {
  simulateYear, simulateFromHourly, posicionSolar,
  normalizarPerdidas, PERDIDAS_DEFECTO, escenariosProbabilidad,
};
